import React from 'react';
import { Modal } from '../../../components/common/Modal';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { supplierService } from '../../../services/supplierService';
import { formatCurrency, formatDecimal } from '../../../utils/format';
import type { InventoryLot } from '../../../types/inventory';
import type { Supplier } from '../../../types/supplier';
import { SupplierForm } from '../../purchasing/SupplierForm';
import { useLanguage } from '../../../features/language/useLanguage';

interface GreenBeanLotsModalProps {
  isOpen: boolean;
  onClose: () => void;
  currencySymbol: string;
  totalKg?: number;
  page: number;
  totalPages: number;
  onPrev: () => void;
  onNext: () => void;
  loading: boolean;
  isError: boolean;
  lots: InventoryLot[];
}

export const GreenBeanLotsModal: React.FC<GreenBeanLotsModalProps> = ({
  isOpen,
  onClose,
  currencySymbol,
  totalKg,
  page,
  totalPages,
  onPrev,
  onNext,
  loading,
  isError,
  lots,
}) => {
  const { t } = useLanguage();
  const [isSupplierModalOpen, setIsSupplierModalOpen] = React.useState(false);
  const [viewingSupplier, setViewingSupplier] = React.useState<Supplier | null>(null);
  const [supplierLoading, setSupplierLoading] = React.useState(false);
  const [supplierError, setSupplierError] = React.useState<string | null>(null);

  const closeSupplierModal = React.useCallback(() => {
    setIsSupplierModalOpen(false);
    setViewingSupplier(null);
    setSupplierError(null);
    setSupplierLoading(false);
  }, []);

  const handleSupplierClick = React.useCallback(async (supplierId?: string) => {
    if (!supplierId) return;

    setIsSupplierModalOpen(true);
    setViewingSupplier(null);
    setSupplierError(null);
    setSupplierLoading(true);

    try {
      const supplier = await supplierService.getById(supplierId);
      if (!supplier) {
        setSupplierError(t('dashboard.greenBeanLotsModal.supplierNotFound'));
        return;
      }
      setViewingSupplier(supplier);
    } catch {
      setSupplierError(t('dashboard.greenBeanLotsModal.supplierLoadFailed'));
    } finally {
      setSupplierLoading(false);
    }
  }, [t]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={t('dashboard.greenBeanLotsModal.title')} maxWidth="6xl">
        <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm text-gray-600">
            {t('dashboard.greenBeanLotsModal.total')}:{' '}
            <span className="font-semibold text-gray-900">
              {typeof totalKg === 'number' ? `${formatDecimal(totalKg, 3)} kg` : '-'}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 disabled:opacity-50"
              onClick={onPrev}
              disabled={page <= 1}
            >
              {t('dashboard.greenBeanLotsModal.prev')}
            </button>
            <div className="text-sm text-gray-600">
              {t('dashboard.greenBeanLotsModal.page')} <span className="font-semibold text-gray-900">{page}</span> / {totalPages}
            </div>
            <button
              type="button"
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 disabled:opacity-50"
              onClick={onNext}
              disabled={page >= totalPages}
            >
              {t('dashboard.greenBeanLotsModal.next')}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-gray-500">{t('dashboard.greenBeanLotsModal.loading')}</div>
        ) : isError ? (
          <div className="text-sm text-red-600">{t('dashboard.greenBeanLotsModal.errorLoad')}</div>
        ) : lots.length === 0 ? (
          <div className="text-sm text-gray-500">{t('dashboard.greenBeanLotsModal.empty')}</div>
        ) : (
          <div className="overflow-x-auto border border-gray-100 rounded-xl">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('dashboard.greenBeanLotsModal.columns.lot')}</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('dashboard.greenBeanLotsModal.columns.lastPurchaseKg')}</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('dashboard.greenBeanLotsModal.columns.remainingKg')}</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('dashboard.greenBeanLotsModal.columns.netPrice')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('purchasing.sampleList.table.supplier')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('reports.general.table.date')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {lots.map((l) => (
                  <tr key={l.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div className="font-medium">{l.itemName || t('settingsMcp.values.none')}</div>
                      <div className="text-xs text-gray-500">{l.lotNumber}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 text-right">{formatDecimal(l.initialQuantity, 3)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right font-semibold">{formatDecimal(l.remainingQuantity, 3)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right font-semibold">
                      {formatCurrency(typeof l.unitPrice === 'number' ? l.unitPrice : 0, currencySymbol)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {l.supplierId ? (
                        <button
                          type="button"
                          className="text-primary-600 hover:underline"
                          onClick={() => void handleSupplierClick(l.supplierId)}
                        >
                          {l.supplierName || t('dashboard.greenBeanLotsModal.viewSupplier')}
                        </button>
                      ) : (
                        <span>{l.supplierName || t('settingsMcp.values.none')}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{new Date(l.receivedDate).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </Modal>

      <Modal
        isOpen={isSupplierModalOpen}
        onClose={closeSupplierModal}
        title={viewingSupplier?.name ? `${t('purchasing.sampleList.table.supplier')}: ${viewingSupplier.name}` : t('purchasing.sampleList.table.supplier')}
        maxWidth="xl"
      >
        {supplierLoading ? (
          <LoadingSpinner text={t('purchasing.supplierList.loading')} />
        ) : supplierError ? (
          <div className="text-sm text-red-600">{supplierError}</div>
        ) : viewingSupplier ? (
          <SupplierForm initialData={viewingSupplier} readOnly onCancel={closeSupplierModal} />
        ) : (
          <div className="text-sm text-gray-500">{t('dashboard.greenBeanLotsModal.noSupplierSelected')}</div>
        )}
      </Modal>
    </>
  );
};
